/**
 * Intelligence Hub API - Unified engagement, attribution, and insights
 */
import apiClient from './index'
import type { ApiResponse } from '@/types'

// =============================================================================
// ENGAGEMENT TYPES
// =============================================================================

export interface EngagementScore {
  id: string
  contact_id: string
  company_id: string | null
  current_score: number
  grade: 'A+' | 'A' | 'B' | 'C' | 'D' | 'F'
  velocity: 'rising' | 'stable' | 'declining'
  score_7d_ago: number | null
  score_14d_ago: number | null
  score_30d_ago: number | null
  last_activity_at: string | null
  last_activity_type: string | null
  email_score: number
  call_score: number
  meeting_score: number
  document_score: number
  website_score: number
  social_score: number
  form_score: number
  email_events: number
  call_events: number
  meeting_events: number
  document_events: number
  last_calculated_at: string | null
  events_processed: number
}

export interface EngagementEvent {
  id: string
  contact_id: string | null
  company_id: string | null
  event_type: string
  event_source: string
  source_id: string | null
  points: number
  metadata: Record<string, unknown>
  created_at: string
}

export interface ContactEngagement {
  contact: {
    id: string
    name: string
    email: string
    company_id?: string
  }
  engagement: EngagementScore
}

export interface EngagementSummary {
  total_scored: number
  by_grade: Record<string, number>
  by_velocity: Record<string, number>
  highly_engaged: number
  at_risk: number
  trending_up: number
}

export interface EngagementConfig {
  id: string
  email_open_points: number
  email_click_points: number
  campaign_open_points: number
  campaign_click_points: number
  call_inbound_points: number
  call_outbound_answered_points: number
  meeting_attended_points: number
  document_proposal_view_points: number
  form_submit_points: number
  // ... other config fields
}

// =============================================================================
// ATTRIBUTION TYPES
// =============================================================================

export interface AttributionTouchpoint {
  id: string
  type: string
  source: string | null
  channel: string
  touched_at: string | null
  utm_campaign: string | null
}

export interface AttributionResult {
  id: string
  deal_id: string
  contact_id: string | null
  company_id: string | null
  deal_amount: number
  deal_closed_at: string | null
  deal_status: 'won' | 'lost'
  first_touch_channel: string | null
  first_touch_source: string | null
  first_touch_at: string | null
  last_touch_channel: string | null
  last_touch_source: string | null
  last_touch_at: string | null
  total_touches: number
  days_to_close: number
  unique_channels: number
  first_touch_revenue: number
  last_touch_revenue: number
  channel_touches: Record<string, number>
  channel_revenue_linear: Record<string, number>
}

export interface DealJourney {
  deal: Record<string, unknown> | null
  attribution: AttributionResult | null
  journey: AttributionTouchpoint[]
  total_touches: number
}

export interface ChannelPerformance {
  channel: string
  touches: number
  deals_first_touch: number
  deals_last_touch: number
  deals_influenced: number
  revenue_first_touch: number
  revenue_last_touch: number
  revenue_linear: number
}

export interface CampaignPerformance {
  campaign: string
  channel: string | null
  touches: number
  unique_contacts: number
  deals_influenced: number
  revenue_attributed: number
}

export interface AttributionSummary {
  period: string
  total_revenue_attributed: number
  total_deals: number
  avg_touches_per_deal: number
  avg_days_to_close: number
  top_channels: ChannelPerformance[]
  top_campaigns: CampaignPerformance[]
}

// =============================================================================
// INSIGHTS TYPES
// =============================================================================

export interface Insight {
  id: string
  category: 'campaign' | 'engagement' | 'pipeline' | 'team' | 'timing'
  insight_type: string
  title: string
  description: string | null
  metric_value: number | null
  metric_unit: string | null
  comparison_value: number | null
  comparison_period: string | null
  change_percent: number | null
  impact_score: number
  confidence: number
  sample_size: number
  related_entity_type: string | null
  related_entity_id: string | null
  related_entity_name: string | null
  ai_summary: string | null
  ai_recommendation: string | null
  date_range_start: string | null
  date_range_end: string | null
}

export interface InsightsSummary {
  total_insights: number
  high_impact_count: number
  by_category: Record<string, Insight[]>
  top_insights: Insight[]
}

// =============================================================================
// AI USAGE TYPES
// =============================================================================

export interface AIUsage {
  tier: string
  monthly_limit: number
  tokens_used: number
  tokens_remaining: number
  period_start: string | null
  allow_overage: boolean
}

// =============================================================================
// ENRICHMENT TYPES
// =============================================================================

export interface EnrichmentProvider {
  id: string
  name: string
  display_name: string
  is_active: boolean
  is_configured: boolean
  credits_remaining?: number
  credits_reset_at?: string
  rate_limit_per_minute: number
  rate_limit_per_day: number
  total_requests: number
  successful_requests: number
  failed_requests: number
  last_request_at?: string
  supports_person_enrichment: boolean
  supports_company_enrichment: boolean
  supports_email_finder: boolean
  supports_email_verification: boolean
}

export interface EnrichmentResult {
  id: string
  entity_type: string
  entity_id: string
  contact_id?: string
  company_id?: string
  provider: string
  enrichment_type: string
  status: string
  error_message?: string
  input_data: Record<string, unknown>
  enriched_data: Record<string, unknown>
  fields_updated: string[]
  fields_skipped: string[]
  confidence_score: number
  match_type?: string
  request_duration_ms: number
  created_at: string
}

export interface EmailFinderResult {
  email: string
  confidence: number
  verified: boolean
  sources?: string[]
}

export interface EmailVerifyResult {
  email: string
  valid: boolean
  deliverable: boolean
  disposable: boolean
  accept_all: boolean
  role_based: boolean
  free_provider: boolean
}

// =============================================================================
// LEAD TYPES
// =============================================================================

export interface Lead {
  id: string
  first_name: string
  last_name: string
  full_name: string
  email: string
  email_verified: boolean
  email_confidence: number
  title: string
  seniority_level: string
  department: string
  company_name: string
  company_domain: string
  company_linkedin_url?: string
  industry: string
  employee_count_range: string
  location_city: string
  location_state: string
  location_country: string
  location_timezone?: string
  linkedin_url?: string
  twitter_handle?: string
  phone?: string
  phone_type?: string
  source: string
  source_id?: string
  status: string
  converted_to_contact_id?: string
  converted_at?: string
  fit_score: number
  intent_score: number
  tags: string[]
  list_ids: string[]
  do_not_email: boolean
  do_not_call: boolean
  emails_sent: number
  emails_opened: number
  emails_clicked: number
  last_contacted_at?: string
  created_at: string
  updated_at: string
}

export interface LeadList {
  id: string
  name: string
  description?: string
  list_type: string
  dynamic_filters: Record<string, unknown>
  lead_count: number
  last_count_at?: string
  owner_user_id?: string
  shared_with_user_ids: string[]
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface SavedSearch {
  id: string
  name: string
  description?: string
  search_criteria: LeadSearchCriteria
  use_count: number
  last_used_at?: string
  owner_user_id?: string
  created_at: string
  updated_at: string
}

export interface LeadSearchCriteria {
  titles?: string[]
  industries?: string[]
  locations?: string[]
  company_sizes?: string[]
  seniority_levels?: string[]
  keywords?: string
  has_email?: boolean
  status?: string
}

export interface LeadSearchFilters {
  titles: string[]
  industries: string[]
  locations: string[]
  company_sizes: string[]
  seniority_levels: string[]
  statuses: string[]
}

// =============================================================================
// DASHBOARD TYPES
// =============================================================================

export interface IntelligenceDashboard {
  engagement: EngagementSummary
  attribution: AttributionSummary
  insights: InsightsSummary
}

// =============================================================================
// API METHODS
// =============================================================================

export const intelligenceApi = {
  // =========================================================================
  // ENGAGEMENT
  // =========================================================================

  /**
   * Get contacts sorted by engagement
   */
  async getEngagedContacts(params?: {
    grade?: string
    velocity?: 'rising' | 'stable' | 'declining'
    limit?: number
  }): Promise<{ contacts: ContactEngagement[]; count: number }> {
    const response = await apiClient.get<ApiResponse<{ contacts: ContactEngagement[]; count: number }>>(
      '/intelligence/engagement/contacts',
      { params }
    )
    return response.data.data
  },

  /**
   * Get engagement for a specific contact
   */
  async getContactEngagement(contactId: string): Promise<{
    score: EngagementScore | null
    recent_events: EngagementEvent[]
  }> {
    const response = await apiClient.get<ApiResponse<{
      score: EngagementScore | null
      recent_events: EngagementEvent[]
    }>>(`/intelligence/engagement/contacts/${contactId}`)
    return response.data.data
  },

  /**
   * Get aggregate engagement for a company
   */
  async getCompanyEngagement(companyId: string): Promise<{
    total_score: number
    best_grade: string
    contact_count: number
    contact_scores: Array<{
      contact_id: string
      contact_name: string
      score: number
      grade: string
      velocity: string
    }>
    recent_events: EngagementEvent[]
  }> {
    const response = await apiClient.get<ApiResponse<{
      total_score: number
      best_grade: string
      contact_count: number
      contact_scores: Array<{
        contact_id: string
        contact_name: string
        score: number
        grade: string
        velocity: string
      }>
      recent_events: EngagementEvent[]
    }>>(`/intelligence/engagement/companies/${companyId}`)
    return response.data.data
  },

  /**
   * Get engagement summary
   */
  async getEngagementSummary(): Promise<EngagementSummary> {
    const response = await apiClient.get<ApiResponse<EngagementSummary>>(
      '/intelligence/engagement/summary'
    )
    return response.data.data
  },

  /**
   * Get engagement config
   */
  async getEngagementConfig(): Promise<EngagementConfig> {
    const response = await apiClient.get<ApiResponse<EngagementConfig>>(
      '/intelligence/engagement/config'
    )
    return response.data.data
  },

  /**
   * Update engagement config
   */
  async updateEngagementConfig(config: Partial<EngagementConfig>): Promise<EngagementConfig> {
    const response = await apiClient.put<ApiResponse<EngagementConfig>>(
      '/intelligence/engagement/config',
      config
    )
    return response.data.data
  },

  /**
   * Recalculate engagement scores
   */
  async recalculateEngagement(contactId?: string): Promise<{
    message: string
    score?: EngagementScore
    processed?: number
    errors?: number
  }> {
    const response = await apiClient.post<ApiResponse<{
      message: string
      score?: EngagementScore
      processed?: number
      errors?: number
    }>>('/intelligence/engagement/recalculate', { contact_id: contactId })
    return response.data.data
  },

  // =========================================================================
  // ATTRIBUTION
  // =========================================================================

  /**
   * Get attribution journey for a deal
   */
  async getDealJourney(dealId: string): Promise<DealJourney> {
    const response = await apiClient.get<ApiResponse<DealJourney>>(
      `/intelligence/attribution/deals/${dealId}`
    )
    return response.data.data
  },

  /**
   * Get channel performance
   */
  async getChannelPerformance(params?: {
    days?: number
    start_date?: string
    end_date?: string
  }): Promise<{ channels: ChannelPerformance[]; period: { start: string; end: string } }> {
    const response = await apiClient.get<ApiResponse<{
      channels: ChannelPerformance[]
      period: { start: string; end: string }
    }>>('/intelligence/attribution/channels', { params })
    return response.data.data
  },

  /**
   * Get campaign performance
   */
  async getCampaignPerformance(params?: {
    days?: number
    start_date?: string
    end_date?: string
  }): Promise<{ campaigns: CampaignPerformance[]; period: { start: string; end: string } }> {
    const response = await apiClient.get<ApiResponse<{
      campaigns: CampaignPerformance[]
      period: { start: string; end: string }
    }>>('/intelligence/attribution/campaigns', { params })
    return response.data.data
  },

  /**
   * Get attribution summary
   */
  async getAttributionSummary(): Promise<AttributionSummary> {
    const response = await apiClient.get<ApiResponse<AttributionSummary>>(
      '/intelligence/attribution/summary'
    )
    return response.data.data
  },

  /**
   * Calculate attribution for a deal
   */
  async calculateDealAttribution(dealId: string): Promise<AttributionResult> {
    const response = await apiClient.post<ApiResponse<AttributionResult>>(
      `/intelligence/attribution/calculate/${dealId}`
    )
    return response.data.data
  },

  // =========================================================================
  // INSIGHTS
  // =========================================================================

  /**
   * Get insights
   */
  async getInsights(params?: {
    category?: string
    limit?: number
  }): Promise<{ insights: Insight[]; count: number }> {
    const response = await apiClient.get<ApiResponse<{ insights: Insight[]; count: number }>>(
      '/intelligence/insights',
      { params }
    )
    return response.data.data
  },

  /**
   * Get insights summary
   */
  async getInsightsSummary(): Promise<InsightsSummary> {
    const response = await apiClient.get<ApiResponse<InsightsSummary>>(
      '/intelligence/insights/summary'
    )
    return response.data.data
  },

  /**
   * Refresh insights
   */
  async refreshInsights(days?: number): Promise<{ message: string; count: number }> {
    const response = await apiClient.post<ApiResponse<{ message: string; count: number }>>(
      '/intelligence/insights/refresh',
      { days }
    )
    return response.data.data
  },

  // =========================================================================
  // AI USAGE
  // =========================================================================

  /**
   * Get AI usage
   */
  async getAIUsage(): Promise<AIUsage> {
    const response = await apiClient.get<ApiResponse<AIUsage>>(
      '/intelligence/ai/usage'
    )
    return response.data.data
  },

  /**
   * Update AI limits
   */
  async updateAILimits(params: {
    company_id?: string
    tier?: string
    allow_overage?: boolean
    overage_rate_per_1k?: number
  }): Promise<Record<string, unknown>> {
    const response = await apiClient.put<ApiResponse<Record<string, unknown>>>(
      '/intelligence/ai/limits',
      params
    )
    return response.data.data
  },

  // =========================================================================
  // DASHBOARD
  // =========================================================================

  /**
   * Get combined intelligence dashboard
   */
  async getDashboard(): Promise<IntelligenceDashboard> {
    const response = await apiClient.get<ApiResponse<IntelligenceDashboard>>(
      '/intelligence/dashboard'
    )
    return response.data.data
  },

  // =========================================================================
  // ENRICHMENT
  // =========================================================================

  /**
   * Get all enrichment providers
   */
  async getProviders(): Promise<EnrichmentProvider[]> {
    const response = await apiClient.get<{ providers: EnrichmentProvider[] }>(
      '/intelligence/providers'
    )
    return response.data.providers
  },

  /**
   * Get a specific provider
   */
  async getProvider(providerName: string): Promise<EnrichmentProvider | null> {
    const response = await apiClient.get<{ provider: EnrichmentProvider }>(
      `/intelligence/providers/${providerName}`
    )
    return response.data.provider
  },

  /**
   * Configure a provider
   */
  async configureProvider(data: {
    name: string
    api_key: string
    api_secret?: string
  }): Promise<EnrichmentProvider> {
    const response = await apiClient.post<{ provider: EnrichmentProvider }>(
      '/intelligence/providers',
      data
    )
    return response.data.provider
  },

  /**
   * Delete/disable a provider
   */
  async deleteProvider(providerName: string): Promise<void> {
    await apiClient.delete(`/intelligence/providers/${providerName}`)
  },

  /**
   * Enrich a contact
   */
  async enrichContact(
    contactId: string,
    options?: {
      providers?: string[]
      overwrite_existing?: boolean
    }
  ): Promise<EnrichmentResult> {
    const response = await apiClient.post<{ result: EnrichmentResult }>(
      `/intelligence/enrich/contact/${contactId}`,
      options
    )
    return response.data.result
  },

  /**
   * Enrich a company
   */
  async enrichCompany(
    companyId: string,
    options?: {
      providers?: string[]
      overwrite_existing?: boolean
    }
  ): Promise<EnrichmentResult> {
    const response = await apiClient.post<{ result: EnrichmentResult }>(
      `/intelligence/enrich/company/${companyId}`,
      options
    )
    return response.data.result
  },

  /**
   * Enrich by email only
   */
  async enrichByEmail(
    email: string,
    options?: { providers?: string[] }
  ): Promise<EnrichmentResult> {
    const response = await apiClient.post<{ result: EnrichmentResult }>(
      '/intelligence/enrich/email',
      { email, ...options }
    )
    return response.data.result
  },

  /**
   * Find email for a person
   */
  async findEmail(data: {
    first_name: string
    last_name: string
    company_domain: string
    provider?: string
  }): Promise<EmailFinderResult> {
    const response = await apiClient.post<{ result: EmailFinderResult }>(
      '/intelligence/email/find',
      data
    )
    return response.data.result
  },

  /**
   * Verify an email address
   */
  async verifyEmail(email: string, provider?: string): Promise<EmailVerifyResult> {
    const response = await apiClient.post<{ result: EmailVerifyResult }>(
      '/intelligence/email/verify',
      { email, provider }
    )
    return response.data.result
  },

  /**
   * Get enrichment history for an entity
   */
  async getEnrichmentHistory(
    entityType: 'contact' | 'company',
    entityId: string
  ): Promise<EnrichmentResult[]> {
    const response = await apiClient.get<{ results: EnrichmentResult[] }>(
      `/intelligence/enrichment/${entityType}/${entityId}`
    )
    return response.data.results
  },

  // =========================================================================
  // LEADS
  // =========================================================================

  /**
   * Search leads
   */
  async searchLeads(
    criteria: LeadSearchCriteria & { limit?: number; cursor?: string }
  ): Promise<{ leads: Lead[]; next_cursor?: string; total?: number }> {
    const response = await apiClient.post<{
      leads: Lead[]
      next_cursor?: string
      total?: number
    }>('/intelligence/leads/search', criteria)
    return response.data
  },

  /**
   * Get a lead by ID
   */
  async getLead(leadId: string): Promise<Lead> {
    const response = await apiClient.get<{ lead: Lead }>(`/intelligence/leads/${leadId}`)
    return response.data.lead
  },

  /**
   * Update a lead
   */
  async updateLead(leadId: string, data: Partial<Lead>): Promise<Lead> {
    const response = await apiClient.put<{ lead: Lead }>(
      `/intelligence/leads/${leadId}`,
      data
    )
    return response.data.lead
  },

  /**
   * Delete a lead
   */
  async deleteLead(leadId: string): Promise<void> {
    await apiClient.delete(`/intelligence/leads/${leadId}`)
  },

  /**
   * Convert lead to CRM contact
   */
  async convertLeadToContact(
    leadId: string,
    options?: {
      create_company?: boolean
      company_id?: string
      additional_data?: Record<string, unknown>
    }
  ): Promise<{ contact_id: string; company_id?: string }> {
    const response = await apiClient.post<{ contact_id: string; company_id?: string }>(
      `/intelligence/leads/${leadId}/convert`,
      options
    )
    return response.data
  },

  /**
   * Bulk convert leads to contacts
   */
  async bulkConvertLeads(
    leadIds: string[],
    options?: { create_companies?: boolean }
  ): Promise<{
    converted: number
    failed: number
    contact_ids: string[]
    errors: Array<{ lead_id: string; error: string }>
  }> {
    const response = await apiClient.post<{
      converted: number
      failed: number
      contact_ids: string[]
      errors: Array<{ lead_id: string; error: string }>
    }>('/intelligence/leads/bulk/convert', { lead_ids: leadIds, ...options })
    return response.data
  },

  /**
   * Add tags to leads
   */
  async addLeadTags(leadIds: string[], tags: string[]): Promise<void> {
    await apiClient.post('/intelligence/leads/bulk/tags', {
      lead_ids: leadIds,
      tags,
      action: 'add',
    })
  },

  /**
   * Remove tags from leads
   */
  async removeLeadTags(leadIds: string[], tags: string[]): Promise<void> {
    await apiClient.post('/intelligence/leads/bulk/tags', {
      lead_ids: leadIds,
      tags,
      action: 'remove',
    })
  },

  /**
   * Get available search filters
   */
  async getSearchFilters(): Promise<LeadSearchFilters> {
    const response = await apiClient.get<{ filters: LeadSearchFilters }>(
      '/intelligence/leads/filters'
    )
    return response.data.filters
  },

  // =========================================================================
  // LEAD LISTS
  // =========================================================================

  /**
   * Get all lead lists
   */
  async getLeadLists(): Promise<LeadList[]> {
    const response = await apiClient.get<{ lists: LeadList[] }>('/intelligence/lists')
    return response.data.lists
  },

  /**
   * Create a lead list
   */
  async createLeadList(data: {
    name: string
    description?: string
    list_type?: 'static' | 'dynamic'
    dynamic_filters?: Record<string, unknown>
  }): Promise<LeadList> {
    const response = await apiClient.post<{ list: LeadList }>('/intelligence/lists', data)
    return response.data.list
  },

  /**
   * Get a lead list
   */
  async getLeadList(listId: string): Promise<LeadList> {
    const response = await apiClient.get<{ list: LeadList }>(`/intelligence/lists/${listId}`)
    return response.data.list
  },

  /**
   * Update a lead list
   */
  async updateLeadList(listId: string, data: Partial<LeadList>): Promise<LeadList> {
    const response = await apiClient.put<{ list: LeadList }>(
      `/intelligence/lists/${listId}`,
      data
    )
    return response.data.list
  },

  /**
   * Delete a lead list
   */
  async deleteLeadList(listId: string): Promise<void> {
    await apiClient.delete(`/intelligence/lists/${listId}`)
  },

  /**
   * Get leads in a list
   */
  async getLeadsInList(
    listId: string,
    options?: { limit?: number; cursor?: string }
  ): Promise<{ leads: Lead[]; next_cursor?: string }> {
    const params = new URLSearchParams()
    if (options?.limit) params.set('limit', options.limit.toString())
    if (options?.cursor) params.set('cursor', options.cursor)

    const url = params.toString()
      ? `/intelligence/lists/${listId}/leads?${params.toString()}`
      : `/intelligence/lists/${listId}/leads`

    const response = await apiClient.get<{ leads: Lead[]; next_cursor?: string }>(url)
    return response.data
  },

  /**
   * Add leads to a list
   */
  async addLeadsToList(listId: string, leadIds: string[]): Promise<void> {
    await apiClient.post(`/intelligence/lists/${listId}/leads`, { lead_ids: leadIds })
  },

  /**
   * Remove leads from a list
   */
  async removeLeadsFromList(listId: string, leadIds: string[]): Promise<void> {
    await apiClient.delete(`/intelligence/lists/${listId}/leads`, {
      data: { lead_ids: leadIds },
    })
  },

  // =========================================================================
  // SAVED SEARCHES
  // =========================================================================

  /**
   * Get saved searches
   */
  async getSavedSearches(): Promise<SavedSearch[]> {
    const response = await apiClient.get<{ searches: SavedSearch[] }>(
      '/intelligence/searches'
    )
    return response.data.searches
  },

  /**
   * Create a saved search
   */
  async createSavedSearch(data: {
    name: string
    description?: string
    search_criteria: LeadSearchCriteria
  }): Promise<SavedSearch> {
    const response = await apiClient.post<{ search: SavedSearch }>(
      '/intelligence/searches',
      data
    )
    return response.data.search
  },

  /**
   * Get a saved search
   */
  async getSavedSearch(searchId: string): Promise<SavedSearch> {
    const response = await apiClient.get<{ search: SavedSearch }>(
      `/intelligence/searches/${searchId}`
    )
    return response.data.search
  },

  /**
   * Update a saved search
   */
  async updateSavedSearch(
    searchId: string,
    data: Partial<SavedSearch>
  ): Promise<SavedSearch> {
    const response = await apiClient.put<{ search: SavedSearch }>(
      `/intelligence/searches/${searchId}`,
      data
    )
    return response.data.search
  },

  /**
   * Delete a saved search
   */
  async deleteSavedSearch(searchId: string): Promise<void> {
    await apiClient.delete(`/intelligence/searches/${searchId}`)
  },

  /**
   * Run a saved search
   */
  async runSavedSearch(
    searchId: string,
    options?: { limit?: number; cursor?: string }
  ): Promise<{ leads: Lead[]; next_cursor?: string }> {
    const response = await apiClient.post<{ leads: Lead[]; next_cursor?: string }>(
      `/intelligence/searches/${searchId}/run`,
      options
    )
    return response.data
  },
}
